/**
 * ConfirmPanel — Step 3 (parent-only units) or Step 4 (mini-units).
 * Reviews the chosen unit/mini before the wizard fires off generation.
 */
import React from "react";
import { Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const ConfirmPanel = ({ unit, mini, onProceed, onBack }) => {
  const usingMini = !!mini;
  const expectations = usingMini
    ? mini.specific_expectations
    : unit.specific_expectations;
  const stepNumber = usingMini ? "Step 4" : "Step 3";
  const titleLine = usingMini
    ? `${unit.grade} · ${mini.label} · ${mini.name}`
    : `${unit.grade} · Unit ${unit.unit_number} · ${unit.name}`;
  const subLine = usingMini
    ? `Inside Unit ${unit.unit_number} — ${unit.name} · Day budget: ${unit.day_budget} (parent unit)`
    : `Day budget: ${unit.day_budget}`;
  return (
    <div className="ae-card p-6 md:p-8" data-testid="math-engine-unit-confirm">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70">
        {stepNumber} · Confirm what we'll build
      </p>
      <h3 className="mt-1 font-black text-2xl text-[#1c2d3d] tracking-tight">
        {titleLine}
      </h3>
      <p className="text-sm text-[#4a6984]/70 mt-0.5 font-semibold">{subLine}</p>

      {unit.focus && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
            Unit focus
          </p>
          <p className="text-sm text-[#4a6984]/90 leading-relaxed">{unit.focus}</p>
        </div>
      )}

      {expectations.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-2">
            {usingMini ? "Mini-unit expectations" : "Specific expectations covered"}{" "}
            ({expectations.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {expectations.map((c) => (
              <span
                key={c}
                className="inline-flex items-center rounded-full bg-[#96ded1]/20 text-[#1c2d3d] px-2.5 py-0.5 text-[11px] font-bold tracking-tight"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {unit.cross_strand.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-2">
            Cross-strand connections (parent unit)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unit.cross_strand.map((c) => (
              <span
                key={c}
                className="inline-flex items-center rounded-full border border-[#4a6984]/20 text-[#4a6984] px-2.5 py-0.5 text-[11px] font-bold tracking-tight"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {unit.ai_notes && !usingMini && (
        <div className="mt-5 rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7]/40 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
            Teaching notes
          </p>
          <p className="text-xs text-[#4a6984]/90 leading-relaxed whitespace-pre-line">
            {unit.ai_notes}
          </p>
        </div>
      )}

      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button
          type="button"
          onClick={() => onProceed("start_unit")}
          data-testid="math-engine-confirm-start"
          className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white"
        >
          <Sparkles className="h-4 w-4 mr-2" /> Start{" "}
          {usingMini ? "the mini-unit" : "the unit"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => onProceed("activation_only")}
          data-testid="math-engine-confirm-activation"
          className="border-[#4a6984]/20"
        >
          Just the activation
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          data-testid="math-engine-confirm-back"
          className="border-[#4a6984]/20"
        >
          {usingMini ? "Pick a different mini-unit" : "Pick a different unit"}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmPanel;
